import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import styles from "./styles.module.scss";
import PrimaryButton from "../../../../../components/common/PrimaryButton";
import OutlinedButton from "../../../../../components/common/OutlinedButton";
import Table from "../../../../contract/list_v2/Table";
import Money from "../../../../contract/list_v2/Table/components/Money";
import {
  ILockerShipmentSettlementContract,
  ISettleLockerShipmentsData,
} from "../../../../../setup/api/services/lockerShipment/models";
import { formatAmount } from "../../../../../setup/util/wallet";
import { contractColumns, contractColumnVisibility } from "./columns";

interface Props {
  /** What was sent to settle (the full matched list). */
  matched: ILockerShipmentSettlementContract[];
  settlement: ISettleLockerShipmentsData;
  onUploadAnother: () => void;
  onClose: () => void;
}

const sumFees = (contracts: ILockerShipmentSettlementContract[]) =>
  contracts.reduce((sum, c) => sum + (c.shipmentFee || 0), 0);

const SettlementResult = ({
  matched,
  settlement,
  onUploadAnother,
  onClose,
}: Props) => {
  const matchedById = new Map(matched.map((c) => [c.id, c]));
  const settledIds = new Set(settlement.settledContractIds || []);

  // Resolved against the preview list for display; an ID the preview didn't
  // include still gets a (linked) row rather than being dropped silently.
  const settled = Array.from(settledIds).map(
    (id) =>
      matchedById.get(id) ||
      ({ id, shortId: id } as ILockerShipmentSettlementContract)
  );
  const skipped = matched.filter((c) => !settledIds.has(c.id));
  const settledCount = settlement.settledCount ?? settled.length;
  const allSettled = skipped.length === 0 && settledCount > 0;

  return (
    <div className={styles.modalContent}>
      <div className={styles.resultHeader}>
        {allSettled ? (
          <img src="/assets/images/svg/Success.svg" alt="" />
        ) : (
          <ReportProblemOutlinedIcon className={styles.warningIcon} />
        )}
        <h2 className={styles.title}>
          {allSettled
            ? "تمت تسوية رسوم الشحن بنجاح"
            : settledCount > 0
            ? "تمت التسوية جزئياً"
            : "لم تتم تسوية أي معاملة"}
        </h2>
        <p className={styles.subtitle}>
          {allSettled
            ? `تمت تسوية جميع المعاملات المطابقة (${settledCount}) ولن تظهر في عمليات المطابقة القادمة.`
            : `تمت تسوية ${settledCount} من أصل ${matched.length} معاملة. المعاملات المتبقية لم تعد مؤهلة للتسوية.`}
        </p>
      </div>

      <div className={styles.summary}>
        <div className={styles.summaryItem}>
          <span className={styles.summaryLabel}>معاملات تمت تسويتها</span>
          <span className={styles.summaryValue}>{settledCount}</span>
        </div>
        <div className={styles.summaryItem}>
          <span className={styles.summaryLabel}>إجمالي رسوم الشحن المسواة</span>
          <Money amount={formatAmount(sumFees(settled))} />
        </div>
        {skipped.length > 0 && (
          <div className={styles.summaryItem}>
            <span className={styles.summaryLabel}>معاملات لم تتم تسويتها</span>
            <span className={styles.summaryValue}>{skipped.length}</span>
          </div>
        )}
      </div>

      {settled.length > 0 && (
        <>
          <h3 className={styles.sectionTitle}>المعاملات التي تمت تسويتها</h3>
          <div className={styles.tableScroll}>
            <Table
              columns={contractColumns}
              columnVisibility={contractColumnVisibility}
              data={settled}
            />
          </div>
        </>
      )}

      {skipped.length > 0 && (
        <>
          <h3 className={styles.sectionTitle}>المعاملات التي لم تتم تسويتها</h3>
          <p className={styles.empty}>
            تغيرت حالة هذه المعاملات بعد المطابقة (مثلاً تمت تسويتها من مكان
            آخر)، راجع كل معاملة من خلال رقمها.
          </p>
          <div className={styles.tableScroll}>
            <Table
              columns={contractColumns}
              columnVisibility={contractColumnVisibility}
              data={skipped}
            />
          </div>
        </>
      )}

      <div className={styles.buttonContainer}>
        <PrimaryButton width="220px" onClick={onUploadAnother}>
          رفع ملف جديد
        </PrimaryButton>
        <OutlinedButton onClick={onClose}>إغلاق</OutlinedButton>
      </div>
    </div>
  );
};

export default SettlementResult;
